import { Mark, mergeAttributes } from "@tiptap/core";

export interface ProperNounMarkOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    properNounMark: {
      setProperNoun: (attrs?: { termId?: string | null }) => ReturnType;
      toggleProperNoun: (attrs?: { termId?: string | null }) => ReturnType;
      unsetProperNoun: () => ReturnType;
    };
  }
}

/**
 * Inline mark for story-critical proper nouns that should not be translated.
 * Persists in TipTap JSON as `{ type: "properNoun", attrs: { termId? } }`.
 */
export const ProperNounMark = Mark.create<ProperNounMarkOptions>({
  name: "properNoun",

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      termId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-proper-noun-id"),
        renderHTML: (attributes) => {
          if (!attributes.termId) return {};
          return { "data-proper-noun-id": attributes.termId };
        },
      },
    };
  },

  parseHTML() {
    return [
      { tag: "span[data-proper-noun]" },
      { tag: 'span[data-proper-noun-id]' },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-proper-noun": "true",
        class: "manuscript-proper-noun",
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setProperNoun:
        (attrs) =>
        ({ commands }) =>
          commands.setMark(this.name, { termId: attrs?.termId ?? null }),
      toggleProperNoun:
        (attrs) =>
        ({ commands }) =>
          commands.toggleMark(this.name, { termId: attrs?.termId ?? null }),
      unsetProperNoun:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

export default ProperNounMark;
